import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import {
  EXPIRY_CATEGORIES,
  PRODUCT_CATEGORIES,
  PRODUCT_MEASUREMENT_UNITS,
  PRODUCT_STORAGE_OPTIONS,
} from '../../config_and_helpers/config';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import {
  checkCatNum,
  createProduct,
  updateProduct,
  getKitStatus,
} from '../../clients/product_client';
import { getManufacturerSelectList } from '../../clients/manufacturer_client';
import { getSupplierSelectList } from '../../clients/supplier_client';
import globalSupplierSelectCache, {
  isCacheStale,
} from '../../config_and_helpers/supplier_cache';
import * as yup from 'yup';
import { Formik } from 'formik';
import { Col, Form, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import './ProductComponentStyle.css';
import '../Glass/ProcureModalStyle.css';
import '../Glass/GlassStyle.css';
import 'font-awesome/css/font-awesome.min.css';

import { PencilFill, TrashFill, InfoCircle } from 'react-bootstrap-icons';
import {
  showToast,
  updateObjectInObjectArray,
} from '../../config_and_helpers/helpers';
import RequiredAsteriskComponent from '../Generic/RequiredAsteriskComponent';
import S3MediaImage from '../Generic/S3MediaImage';
import S3MediaLink from '../Generic/S3MediaLink';
import { Link } from 'react-router-dom';
import DiscountedPriceComponent from '../Generic/DiscountedPriceComponent';
import CustomProductContextHook from '../../pages/Product/CustomProductContextHook';
import SimilarProductsModal from './SimilarProductsModal';
import DropdownSelect from '../Generic/DropdownSelect';
import ProductKitComponentModal from './ProductKitComponentModal';
import NumberInput from '../Generic/NumberInput';
import {
  parseNumericInput,
  yupNumericField,
  yupNumericString,
} from '../../utils/numberInput';

/**
 * Creates a Yup schema for product form validation based on whether the current user is a supplier or not.
 * Checks that all required fields have been filled in correctly.
 *
 * @function createFormSchema
 * @param {Object} params - The parameters that define the schema.
 * @param {boolean} params.isSupplier - A boolean value that tells whether the currently logged-in user is a supplier.
 * @returns {yup.ObjectSchema} The schema to enforce on the product form.
 */
const createFormSchema = ({ isSupplier }) =>
  yup.object().shape({
    productName: yup
      .string()
      .required('Product name is required')
      .min(2, 'Product name must be at least 2 characters long')
      .max(250, 'Product name must be at most 250 characters long'),
    catalogueNumber: yup
      .string()
      .required('Catalogue number is required')
      .min(2, 'Catalogue number must be at least 2 characters long')
      .test(
        'is-english',
        'CAT# must be made of English letters and numbers.',
        value => {
          return /^[a-zA-Z0-9\-_. /]+$/.test(value);
        }
      ),
    category: yup.string().required('Product category is required'),
    unit: yup.string().required('Measurement unit is required'),
    unitQuantity: yupNumericString({
      required: true,
      min: 0,
      message: 'Unit volume/quantity must be a positive number',
    }),
    unitsPerSubUnit: yupNumericString({
      required: false,
      min: 0,
      message: 'Unit volume/quantity must be a positive number',
    }),
    storageConditions: yup.string().required('Storage condition is required'),
    aliquotsLocation: yup
      .string()
      .max(255, 'Aliquots location cannot exceed 255 characters')
      .nullable()
      .notRequired(),
    stock: yupNumericString({
      required: false,
      integerOnly: true,
      min: 0,
      message: 'Current stock must be a positive number',
    }),
    price: yup.lazy(() =>
      !isSupplier
        ? yupNumericString({
            required: false,
            message: 'Current price must be a valid number',
          })
        : yup.mixed().notRequired()
    ),
    discount: yup.lazy(() =>
      !isSupplier
        ? yupNumericField({
            required: false,
            min: 1,
            max: 100,
            typeError: 'Discount percentage must be a valid number',
          }).nullable()
        : yup.mixed().notRequired()
    ),
    supplier: yup.lazy(value =>
      !isSupplier
        ? yup.string().required('Supplier is required')
        : yup.mixed().notRequired()
    ),
    productUrl: yup
      .string()
      .url('Enter a valid URL')
      .max(500, 'URL exceeds permitted length'),
    notes: yup.string().max(255, 'Notes cannot exceed 255 characters'),
    expiryRelevant: yup.string().required('Is expiry relevant is required'),
  });

/**
 * Represents a modal component for creating/editing a product.
 * @param {function} onSuccessfulSubmit - The function to be called when the form is successfully submitted.
 * @param {Object} productObj - The object containing the details of the product being edited (optional).
 * @param {boolean} homeShowModal - Indicates whether to display the modal (optional).
 * @param {function} setHomeShowModal - The function to handle the modal display state (optional).
 * @param {function} handleProductUpdateFromSupplierTable - The function to handle product update via a supplier table (optional).
 * @param {function} clearSearchValue - The function to clear the search value (optional).
 */
const ProductModal = ({
  onSuccessfulSubmit,
  productObj,
  homeShowModal,
  setHomeShowModal,
  clearSearchValue,
  handleProductUpdateFromSupplierTable,
  onEditButtonHover,
  isEditButtonLoading,
}) => {
  //   a custom hook to fetch the products for the ProductContext, else return fall back data
  const { products, setProducts } = CustomProductContextHook();

  // useRef to manage the showing of a spinner when fetching the manufacturers list
  const fetchingManufacturersRef = useRef(false);

  // Retrieves context values including the authentication token and user role information.
  const { token, isSupplier, userDetails } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  // Generates a form validation schema based on the user's role (isSupplier). This schema is used to validate the product form inputs.
  const formSchema = createFormSchema({
    isSupplier,
  });

  // Indicates whether the Catalogue Number (CAT#) entered is unique. Initialized as `true` by default.
  const [isCatNumUnique, setIsCatNumUnique] = useState(true);
  // Stores the value of Catalogue Number (CAT#). Initialized as empty string by default.
  const [catalogueNumber, setCatalogueNumber] = useState('');
  // Indicates whether the system is in the process of checking for the uniqueness of the Catalogue Number (CAT#). Initialized as `false` by default.
  const [isCheckingCatNum, setIsCheckingCatNum] = useState(false);

  // Ref for debounce timeout
  const debounceTimeoutRef = useRef(null);
  // Track when we last loaded from cache to detect invalidation
  const lastCacheLoadTimeRef = useRef(null);
  // Stores the list of available manufacturers retrieved from the backend. Initialized as `null` by default.
  const [manufacturerList, setManufacturerList] = useState([]);
  // Stores the list of available suppliers retrieved from the backend. Initialized as `null` by default.
  const [supplierList, setSupplierList] = useState([]);
  // Stores the Supplier ID for fetching  the filtered manufacturers accordingly
  const [supplierId, setSupplierId] = useState(
    productObj?.supplier?.id || ''
  );
  /* Stores the images related to the product.
    If the product object is provided, it stores the images of the product, otherwise, an empty array is used as default. */
  const [images, setImages] = useState(productObj?.images || []);
  /* Controls whether the ProductModal is displayed or not.
    If the homeShowModal is provided, it uses the value otherwise default to `false`. */
  const [showModal, setShowModal] = useState(
    homeShowModal ? homeShowModal : false
  );
  // Indicates whether the form is being submitted or not. Initialized as `false` by default.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubUnitsField, setShowSubUnitsField] = useState(false); // Indicates whether to present the subunits form field
  const [showDiscountField, setShowDiscountField] = useState(false); // Indicates whether to present the discount form field
  const [similarProducts, setSimilarProducts] = useState(
    productObj?.competing_products || []
  ); // State to manage the array of similar products

  // Kit component management state
  const [showKitComponentModal, setShowKitComponentModal] = useState(false);
  const [editingKitComponent, setEditingKitComponent] = useState(null);
  const [kitComponents, setKitComponents] = useState(
    productObj?.kit_components || []
  );

  // Multi-component management state
  const [showMultiComponentModal, setShowMultiComponentModal] = useState(false);
  const [editingMultiComponent, setEditingMultiComponent] = useState(null);
  const [multiComponents, setMultiComponents] = useState(
    productObj?.multi_components || []
  );

  // useEffect hook to initialize the supplier list
  useEffect(() => {
    // Only fetch supplier list when modal is opened
    if (showModal) {
      // Check if cache is stale (invalidated OR older than 1 minute)
      const cacheIsStale = isCacheStale(lastCacheLoadTimeRef.current);

      // If cache is stale, always fetch fresh data (even if cache has data)
      if (cacheIsStale) {
        // Clear the cache data to force fresh fetch
        globalSupplierSelectCache.data = null;
        globalSupplierSelectCache.promise = null;
      }

      // Use global cache if available AND not stale
      if (globalSupplierSelectCache.data && !cacheIsStale) {
        // Update last load time
        lastCacheLoadTimeRef.current = Date.now();
        setSupplierList(globalSupplierSelectCache.data);
        return;
      }

      // If already fetching, wait for the promise
      if (
        globalSupplierSelectCache.isFetching &&
        globalSupplierSelectCache.promise
      ) {
        globalSupplierSelectCache.promise.then(() => {
          if (globalSupplierSelectCache.data) {
            lastCacheLoadTimeRef.current = Date.now();
            setSupplierList(globalSupplierSelectCache.data);
          }
        });
        return;
      }

      // Fetch and cache the data
      globalSupplierSelectCache.isFetching = true;
      globalSupplierSelectCache.promise = getSupplierSelectList(token, data => {
        globalSupplierSelectCache.data = data;
        globalSupplierSelectCache.timestamp = Date.now();
        globalSupplierSelectCache.isFetching = false;
        lastCacheLoadTimeRef.current = Date.now();
        setSupplierList(data);
      });

      globalSupplierSelectCache.promise.then(() => {
        globalSupplierSelectCache.isFetching = false;
      });
    }
  }, [showModal, token]);

  // useEffect hook to fetch the filtered manufacturer list related to the selected supplier
  useEffect(() => {
    if (supplierId && showModal) {
      fetchingManufacturersRef.current = true;
      getManufacturerSelectList(token, setManufacturerList, supplierId).then(
        response => {
          if (response && response.success) {
            fetchingManufacturersRef.current = false;
          }
        }
      );
    }
  }, [supplierId, token, showModal]);

  // useEffect in charge of setting the showDiscountField and ShowSubUnitsField states
  // in case of editing a product and these values exist
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (productObj && productObj.discount) setShowDiscountField(true);
    if (productObj && productObj.units_per_sub_unit) setShowSubUnitsField(true);
  }, [productObj]);

  // Object for validating the uniqueness of the catalogue number.
  // It contains an id, error text, and a validation function.
  const catalogueNumberUniqueValidator = {
    id: 'unique', // Identifier for this specific validator.
    text: 'This CAT# is already taken.', // Error message displayed if validation fails.
    validate: () => (isCheckingCatNum ? true : isCatNumUnique), // Validation logic.
  };

  // useEffect hook for setting images when the product object changes.
  // The dependency array contains 'productObj', so this effect will rerun every time 'productObj' changes.
  // Preserves existing images with S3 URLs if the new productObj doesn't have complete image data.
  // Checks the products array for updated product data if productObj is missing images.
  useEffect(() => {
    if (productObj) {
      const newImages = productObj.images || [];
      const currentImages = images || [];

      // If new productObj has images with URLs, use them
      if (newImages.length > 0 && newImages.some(img => img.image_url)) {
        setImages(newImages);
      } else {
        // productObj doesn't have complete images, check products array for updated product
        const productInArray = products.find(p => p.id === productObj.id);
        const arrayImages = productInArray?.images || [];
        const arrayImagesWithUrls = arrayImages.filter(img => img.image_url);

        if (arrayImagesWithUrls.length > 0) {
          // Products array has images with URLs, use them
          setImages(arrayImages);
        } else if (
          currentImages.length > 0 &&
          currentImages.some(img => img.image_url)
        ) {
          // Current images have URLs, preserve them (only if same product)
          if (currentImages[0]?.id && productObj.id) {
            // Check if current images belong to this product
            const currentImageBelongsToProduct = currentImages.some(img =>
              productInArray?.images?.some(arrImg => arrImg.id === img.id)
            );
            if (!currentImageBelongsToProduct) {
              // Current images belong to different product, reset
              setImages(newImages);
            }
            // Otherwise, don't update - keep current images
          }
          // Otherwise, can't verify, preserve current images
        } else {
          // No images with URLs available, use productObj images
          setImages(newImages);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productObj, products]);

  // useEffect hook to fetch kit components when editing a kit product
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchKitComponents = async () => {
      // Only fetch if we have a product object, it's a kit, and we're in edit mode (has ID)
      if (
        productObj &&
        productObj.category === 'Kit' &&
        productObj.id &&
        showModal
      ) {
        try {
          const kitStatus = await getKitStatus(token, productObj.id);
          if (kitStatus.kit_components && kitStatus.kit_components.length > 0) {
            setKitComponents(kitStatus.kit_components);
          } else {
            setKitComponents([]);
          }
        } catch (error) {
          setKitComponents([]);
        }
      } else if (
        productObj &&
        productObj.category === 'Kit' &&
        !productObj.id
      ) {
        // New kit product, no components yet
        setKitComponents([]);
      }
    };

    fetchKitComponents();
  }, [productObj, showModal, token]);

  // useEffect hook to fetch multi-components when editing a product with multi-component support
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchMultiComponents = async () => {
      // Only fetch if we have a product object, it supports multi-components, and we're in edit mode (has ID)
      if (
        productObj &&
        productObj.supports_multi_components &&
        productObj.id &&
        showModal
      ) {
        try {
          // Assuming getKitStatus returns the multi_components array
          const multiComponentStatus = await getKitStatus(token, productObj.id);
          if (
            multiComponentStatus.multi_components &&
            multiComponentStatus.multi_components.length > 0
          ) {
            setMultiComponents(multiComponentStatus.multi_components);
          } else {
            setMultiComponents([]);
          }
        } catch (error) {
          setMultiComponents([]);
        }
      } else if (
        productObj &&
        productObj.supports_multi_components &&
        !productObj.id
      ) {
        // New product with multi-component support, no components yet
        setMultiComponents([]);
      }
    };

    fetchMultiComponents();
  }, [productObj, showModal, token]);

  // Async function for validating the catalogue number.
  // Checks if the given catalogue number is unique.
  const validateCatNum = useCallback(
    async value => {
      const response = await checkCatNum(token, value); // Calls API to validate catalogue number.
      setIsCheckingCatNum(false); // Sets state to indicate checking is complete.
      setIsCatNumUnique(response); // Sets state based on the API response.
    },
    [token]
  );

  // Debounced function for catalogue number validation.
  // Debouncing prevents the validateCatNum function from being called too frequently.
  // This is useful for operations like real-time validation where you don't want to overload the server with requests.
  const debouncedCheckCatNum = useCallback(
    value => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        validateCatNum(value);
      }, 1500);
    },
    [validateCatNum]
  );

  // useEffect hook for debouncing catalogue number validation.
  // It's triggered when 'catalogueNumber' or 'debouncedCheckCatNum' changes.
  // This effect manages the logic for when to trigger the catalogue number validation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (catalogueNumber && catalogueNumber !== productObj?.cat_num) {
      debouncedCheckCatNum(catalogueNumber); // Validates the new catalogue number after debounce period.
    } else {
      setIsCheckingCatNum(false); // Resets the checking state if conditions are not met.
    }
  }, [catalogueNumber, debouncedCheckCatNum, productObj]);

  // Cleanup effect for debounce timeout
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Function to handle file changes (typically from an input element).
  // It processes the selected files and updates the 'images' state.
  const handleFileChange = files => {
    // Maps each file to an object containing the file and a temporary unique ID.
    // The ID is generated using the current timestamp and a random number to ensure uniqueness.
    const newImages = files.map(file => ({
      file,
      id: `temp-${Date.now()}-${Math.random()}`,
    }));

    // Updates the 'images' state by adding the new images to the existing ones.
    setImages(prevState => [...prevState, ...newImages]);
  };

  // Function to handle the deletion of an image.
  // It takes the ID of the image to be deleted and updates the 'images' state.
  function handleDeleteImage(imageId) {
    // Filters out the image with the specified ID and updates the state.
    // This effectively removes the image from the UI.
    setImages(prevImages => prevImages.filter(img => img.id !== imageId));
  }

  // Function to handle the closing of the modal.
  // It resets the state related to the modal and images.
  const handleClose = () => {
    setShowModal(false); // Closes the modal by setting the state to false.
    // Optionally closes another modal (e.g., a parent or related modal) if required.
    if (setHomeShowModal) setHomeShowModal(false);
    // Resets the 'images' state to the initial images of the product object,
    // or to an empty array if no product object is present.
    setImages(productObj ? productObj.images : []);
    // Reset the catalogue number value
    setCatalogueNumber('');
    if (!productObj) {
      setShowSubUnitsField(false);
      setShowDiscountField(false);
    }
    setSimilarProducts([]);
    setSupplierId('');
    // Don't reset kit components here - they will be set by the useEffect when productObj changes
    setShowKitComponentModal(false);
    setEditingKitComponent(null);
    setMultiComponents([]); // Reset multi-components
    setEditingMultiComponent(null);
  };

  // Function to handle the action to show the modal.
  // It simply sets the 'showModal' state to true, which is typically used to render the modal.
  const handleShow = () => setShowModal(true);

  // Kit component management functions
  const handleAddKitComponent = () => {
    setEditingKitComponent(null);
    setShowKitComponentModal(true);
  };

  const handleEditKitComponent = component => {
    setEditingKitComponent(component);
    setShowKitComponentModal(true);
  };

  const handleKitComponentUpdate = (action, componentId, componentData) => {
    switch (action) {
      case 'add':
        const newComponent = {
          component: {
            id: componentData.component_id,
            name: componentData.component_name,
            cat_num: componentData.component_cat_num,
          },
          quantity_per_kit: componentData.quantity_per_kit,
          is_required: componentData.is_required,
          available_kits: 0, // Will be calculated when product is saved
        };
        setKitComponents(prev => [...prev, newComponent]);
        break;

      case 'update':
        setKitComponents(prev =>
          prev.map(comp =>
            comp.component.id === componentId
              ? {
                  ...comp,
                  quantity_per_kit: componentData.quantity_per_kit,
                  is_required: componentData.is_required,
                }
              : comp
          )
        );
        break;

      case 'remove':
        setKitComponents(prev =>
          prev.filter(comp => comp.component.id !== componentId)
        );
        break;

      default:
        break;
    }
  };

  const handleKitComponentModalClose = () => {
    setShowKitComponentModal(false);
    setEditingKitComponent(null);
  };

  // Multi-component management functions
  const handleAddMultiComponent = () => {
    setEditingMultiComponent(null);
    setShowMultiComponentModal(true);
  };

  const handleEditMultiComponent = component => {
    setEditingMultiComponent(component);
    setShowMultiComponentModal(true);
  };

  const handleMultiComponentUpdate = (action, componentId, componentData) => {
    switch (action) {
      case 'add':
        // Add new component to the multiComponents array
        if (componentData) {
          const newComponent = {
            component: {
              id: componentData.component_id,
              name: componentData.component_name,
              cat_num: componentData.component_cat_num,
              unit: componentData.component_unit || 'unit',
              description: componentData.component_description || '',
            },
            quantity_per_component: componentData.quantity_per_component,
            is_required:
              componentData.is_required !== undefined
                ? componentData.is_required
                : true,
          };
          setMultiComponents(prev => [...prev, newComponent]);
        }
        break;
      case 'update':
        // Update existing component
        if (componentData && componentId) {
          setMultiComponents(prev =>
            prev.map(comp =>
              comp.component.id === componentId
                ? {
                    component: {
                      id: componentData.component_id,
                      name: componentData.component_name,
                      cat_num: componentData.component_cat_num,
                      unit: componentData.component_unit || 'unit',
                      description: componentData.component_description || '',
                    },
                    quantity_per_component:
                      componentData.quantity_per_component,
                    is_required:
                      componentData.is_required !== undefined
                        ? componentData.is_required
                        : true,
                  }
                : comp
            )
          );
        }
        break;
      case 'remove':
        setMultiComponents(prev =>
          prev.filter(comp => comp.component.id !== componentId)
        );
        break;
      default:
        break;
    }
  };

  const handleMultiComponentModalClose = () => {
    setShowMultiComponentModal(false);
    setEditingMultiComponent(null);
  };

  // Function used to toggle the ShowSubUnitsField boolean state
  const toggleShowSubUnitsField = () => {
    setShowSubUnitsField(prevState => !prevState);
  };

  // Function used to toggle the ShowDiscountField boolean state
  const toggleShowDiscountField = () => {
    setShowDiscountField(prevState => !prevState);
  };

  // Function to handle the submission of the product form.
  // It processes the form values, manages image data, and sends the data to the server.
  const handleSubmit = (values, setFieldError) => {
    setIsSubmitting(true); // Sets a flag indicating that the form is currently being submitted.

    let imagesToDelete = null; // Initializes a variable to track images that need to be deleted.

    const competingProductsIds = similarProducts.map(product => product.id);

    // Constructs the product data object from the form values.
    const productData = {
      name: values.productName,
      cat_num: values.catalogueNumber,
      category: values.category,
      unit: values.unit,
      unit_quantity:
        parseNumericInput(values.unitQuantity) ?? values.unitQuantity,
      units_per_sub_unit:
        parseNumericInput(values.unitsPerSubUnit) ?? values.unitsPerSubUnit,
      storage: values.storageConditions,
      aliquots_location:
        values.aliquotsLocation === null ? null : values.aliquotsLocation,
      stock: parseNumericInput(values.stock, { integerOnly: true }) ?? values.stock,
      price:
        parseNumericInput(values.price) != null
          ? String(parseNumericInput(values.price))
          : values.price,
      discount:
        values.discount === '' || values.discount == null
          ? values.discount
          : parseNumericInput(values.discount) ?? values.discount,
      currency: values.currency,
      url: values.productUrl,
      manufacturer: values.manufacturer,
      notes: values.notes,
      kit_description: values.kitDescription,
      expiry_relevant: values.expiryRelevant,
      competing_products: competingProductsIds,
    };

    // Add kit components data if this is a kit product
    if (values.category === 'Kit' && (kitComponents || []).length > 0) {
      productData.kit_components = (kitComponents || []).map(comp => ({
        component_id: comp.component.id,
        quantity_per_kit: comp.quantity_per_kit,
        is_required: comp.is_required,
      }));
    }

    // Add multi-component data if supports_multi_components is enabled
    if (values.supportsMultiComponents && (multiComponents || []).length > 0) {
      // Validate that all components have valid IDs
      const validComponents = multiComponents.filter(
        comp =>
          comp.component &&
          comp.component.id &&
          comp.component.name &&
          comp.component.cat_num
      );

      if (validComponents.length > 0) {
        // Send multi-component data in the format expected by backend
        productData.supports_multi_components = 'true';

        validComponents.forEach((comp, index) => {
          productData[`multi_components[${index}][component_id]`] =
            comp.component.id.toString();
          productData[`multi_components[${index}][quantity_per_component]`] =
            comp.quantity_per_component.toString();
          productData[`multi_components[${index}][is_required]`] =
            comp.is_required ? 'true' : 'false';
        });
      } else {
        productData.supports_multi_components = 'true';
      }
    } else if (values.supportsMultiComponents) {
      // If multi-component is enabled but no components added, still send the flag
      productData.supports_multi_components = 'true';
    }

    // Sets the supplier ID based on whether the user is a supplier or not.
    productData.supplier = isSupplier
      ? userDetails.supplier_id
      : values.supplier;

    // Flags the product as a supplier catalog item if the user is a supplier.
    productData.supplier_cat_item = !!isSupplier;

    // If updating an existing product, identifies which images to delete.
    if (productObj) {
      imagesToDelete = productObj.images
        .filter(obj1 => !(images || []).some(obj2 => obj1.id === obj2.id))
        .map(obj => obj.id);
      if (imagesToDelete) {
        productData.images_to_delete = JSON.stringify(imagesToDelete);
      }
    }

    // Filters for new images and prepares their data for submission.
    const newImages = (images || []).filter(image => image.file);
    if (newImages.length) {
      const imageInfo = newImages.map(image => ({
        id: image.id,
        type: image.file.type,
      }));
      productData.images = JSON.stringify(imageInfo);
    }

    // Chooses between updating or creating a product based on the existence of 'productObj'.
    const productPromise = productObj
      ? updateProduct(token, productObj.id, productData, newImages)
      : createProduct(token, productData, newImages);

    // Handles the response from the product creation or update request.
    productPromise
      .then(response => {
        if (response && response.success) {
          setTimeout(() => {
            // Callback function on successful submission.
            if (productObj) {
              // Check if images were edited (new images added or images deleted)
              const newImages = (images || []).filter(image => image.file);
              const imagesToDelete = productObj.images
                .filter(
                  obj1 => !(images || []).some(obj2 => obj1.id === obj2.id)
                )
                .map(obj => obj.id);

              const imagesWereEdited =
                newImages.length > 0 || imagesToDelete.length > 0;

              // If images were NOT edited, check if we need to preserve images
              let productToUpdate = response.updatedProduct;
              if (!imagesWereEdited) {
                const responseImages = response.updatedProduct?.images || [];

                // Check if response images are complete (have URLs)
                const responseImagesComplete =
                  responseImages.length > 0 &&
                  responseImages.every(img => img.image_url);

                // Use images from state (which should have S3 URLs from productObj)
                const currentImages = images || [];
                const currentImagesWithUrls = currentImages.filter(
                  img => img.image_url
                );

                if (responseImagesComplete) {
                  // Response has complete images with URLs, use them (server is correct)
                  // productToUpdate already has response.updatedProduct, which includes images
                } else if (
                  responseImages.length === 0 &&
                  currentImagesWithUrls.length > 0
                ) {
                  // Response has no images, preserve from state
                  productToUpdate = {
                    ...response.updatedProduct,
                    images: currentImages,
                  };
                } else if (currentImagesWithUrls.length > 0) {
                  // Response images are incomplete (missing URLs), preserve from state
                  productToUpdate = {
                    ...response.updatedProduct,
                    images: currentImages, // Use images from state which have S3 URLs
                  };
                }
                // Otherwise, use response as-is (no better option available)
              }

              // Ensure first_image is set from images array for table display
              if (productToUpdate.images && productToUpdate.images.length > 0) {
                const firstImage = productToUpdate.images[0];
                if (firstImage.image_url) {
                  productToUpdate.first_image = firstImage.image_url;
                }
              }

              // Update products array
              const updatedProducts = updateObjectInObjectArray(
                products,
                productObj.id,
                productToUpdate
              );

              setProducts(updatedProducts);
            } else {
              if (clearSearchValue) clearSearchValue();
            }
            if (onSuccessfulSubmit) onSuccessfulSubmit();
            if (handleProductUpdateFromSupplierTable)
              handleProductUpdateFromSupplierTable();
            // If the clearSearchValue was passed, call it to clear the search value in the parent component
            response.toast(); // Triggers a success toast message.
            handleClose(); // Closes the modal.
            setManufacturerList([]);
            setIsSubmitting(false); // Resets the submitting state.
          }, 1000);
        } else {
          // Displays an error toast if the submission fails.
          showToast(
            'An unexpected error occurred. Please try again in a little while.',
            'error',
            'top-right',
            3000
          );
          setIsSubmitting(false); // Resets the submitting state.
        }
      })
      .catch(error => {
        const aliquotsLocationErrors = error?.response?.data?.aliquots_location;
        if (Array.isArray(aliquotsLocationErrors) && setFieldError) {
          setFieldError('aliquotsLocation', aliquotsLocationErrors[0]);
        }
        setIsSubmitting(false);
      });
  };

  if (isViewer) {
    return null;
  }

  return (
    <>
      {/* Conditional rendering of the button based on 'homeShowModal' state.
      Displays an edit button if 'productObj' exists, otherwise a create button. */}
      {!homeShowModal && (
        <Button
          variant={productObj ? 'outline-success' : 'success'}
          onClick={handleShow}
          onMouseEnter={onEditButtonHover}
        >
          {productObj ? <PencilFill /> : 'Create Product'}
        </Button>
      )}

      {/* Modal component that displays the form for creating or editing a product. */}
      <Modal
        show={showModal}
        onHide={handleClose}
        backdrop="static"
        dialogClassName="procure-modal-glass procure-modal-glass-product"
      >
        <Modal.Header closeButton className="procure-modal__header">
          <Modal.Title>
            {productObj
              ? `Edit Product ${
                  productObj.name ||
                  productObj.cat_num ||
                  `#${productObj.id}`
                }`
              : 'Create Product'}
          </Modal.Title>
        </Modal.Header>

        {/* Formik component for form management, including validation and submission. */}
        <Formik
          initialValues={{
            // Initial values are set based on 'productObj' if editing, otherwise they're empty.
            productName: productObj ? productObj.name || '' : '',
            catalogueNumber: productObj ? productObj.cat_num || '' : '',
            category: productObj ? productObj.category || '' : '',
            unit: productObj ? productObj.unit || '' : '',
            unitQuantity: productObj ? productObj.unit_quantity || '' : '',
            unitsPerSubUnit:
              productObj && productObj?.units_per_sub_unit !== null
                ? productObj?.units_per_sub_unit || ''
                : '',
            storageConditions: productObj ? productObj.storage || '' : '',
            aliquotsLocation: productObj
              ? productObj.aliquots_location || ''
              : '',
            stock: productObj ? productObj.stock || '' : '',
            price:
              productObj && productObj?.price !== null
                ? productObj.price || ''
                : '',
            discount:
              productObj && productObj?.discount !== null
                ? productObj.discount || ''
                : '',
            currency:
              productObj && productObj?.currency !== null
                ? productObj.currency || ''
                : '',
            manufacturer:
              productObj && productObj?.manufacturer !== null
                ? productObj?.manufacturer?.id || ''
                : '',
            supplier: productObj ? productObj.supplier?.id || '' : '',
            productUrl: productObj ? productObj.url || '' : '',
            productImages: null,
            notes: productObj ? productObj?.notes || '' : '',
            kitDescription: productObj ? productObj?.kit_description || '' : '',
            expiryRelevant: productObj ? productObj?.expiry_relevant || '' : '',
            supportsMultiComponents:
              productObj?.supports_multi_components || false,
            supplierCatItem: productObj
              ? !!productObj?.supplier_cat_item
              : !!isSupplier,
          }}
          validationSchema={formSchema} // Validation schema for the form fields.
          onSubmit={(values, { setFieldError }) => {
            handleSubmit(values, setFieldError); // Submission handler for the form.
          }}
          // Sets which fields are initially touched if editing an existing product.
          initialTouched={
            productObj
              ? {
                  productName: !!productObj.productName,
                  catalogueNumber: !!productObj.catalogueNumber,
                  category: !!productObj.category,
                  unit: !!productObj.unit,
                  unitQuantity: !!productObj.unitQuantity,
                  storageConditions: !!productObj.storageConditions,
                  aliquotsLocation: !!productObj.aliquots_location,
                  stock: !!productObj.stock,
                  price: !!productObj.price,
                  discount: !!productObj.discount,
                  currency: !!productObj.currency,
                  manufacturer: !!productObj.manufacturer,
                  supplier: !!productObj.supplier,
                  productUrl: !!productObj.productUrl,
                  productImages: !!productObj.productImages,
                  notes: !!productObj.notes,
                  expiryRelevant: !!productObj.expiryRelevant,
                  supportsMultiComponents:
                    !!productObj.supports_multi_components,
                  supplierCatItem: !!productObj.supplier_cat_item,
                }
              : {}
          }
          validateOnMount={!!productObj} // Enables validation on mount for editing.
          enableReinitialize={true} // Allows reinitializing values when 'productObj' changes.
        >
          {/* Form rendering and handling logic. */}
          {({
            // Destructuring various helpers and states provided by Formik.
            handleSubmit,
            handleChange,
            values,
            handleBlur,
            touched,
            errors,
            isValid,
            dirty,
            setFieldValue,
            setFieldTouched,
          }) => {
            return (
              <Form id="productForm" noValidate onSubmit={handleSubmit}>
                <Modal.Body className="procure-modal__body d-flex flex-column p-4">
                  {/* Show loading spinner if data is still being fetched */}
                  {isEditButtonLoading && productObj ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: '200px' }}
                    >
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </div>
                  ) : (
                    <>
                      {/* Form groups for various product attributes. */}
                      {/* Each group handles its own validation and display logic. */}
                      <Form.Group
                        controlId="formProductName"
                        className="field-margin"
                      >
                        <Form.Label>
                          Product Name <RequiredAsteriskComponent />
                        </Form.Label>
                        {/* Input for product name with validation feedback. */}
                        <Form.Control
                          type="text"
                          name="productName"
                          value={values.productName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={
                            touched.productName && !!errors.productName
                          }
                          isValid={!errors.productName && values.productName}
                        />
                        {/* Feedback for valid or invalid input. */}
                        <Form.Control.Feedback type="valid">
                          Looks good!
                        </Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          {errors.productName}
                        </Form.Control.Feedback>
                      </Form.Group>
                      <SimilarProductsModal
                        similarProducts={similarProducts}
                        setSimilarProducts={setSimilarProducts}
                      />
                      {/* Kit Component Modal */}
                      <ProductKitComponentModal
                        show={showKitComponentModal}
                        onHide={handleKitComponentModalClose}
                        onComponentUpdate={handleKitComponentUpdate}
                        isEditMode={!!editingKitComponent}
                        componentToEdit={editingKitComponent}
                        existingComponents={kitComponents}
                      />
                      {/* Multi-Component Modal */}
                      <ProductKitComponentModal
                        show={showMultiComponentModal}
                        onHide={handleMultiComponentModalClose}
                        onComponentUpdate={handleMultiComponentUpdate}
                        isEditMode={!!editingMultiComponent}
                        componentToEdit={editingMultiComponent}
                        existingComponents={multiComponents}
                      />
                      <Form.Group
                        controlId="formCatalogueNumber"
                        className="field-margin"
                      >
                        <Form.Label>
                          Catalogue Number <RequiredAsteriskComponent />
                        </Form.Label>
                        {/* Input for catalogue number with validation feedback. */}
                        <Form.Control
                          type="text"
                          name="catalogueNumber"
                          value={values.catalogueNumber}
                          onChange={event => {
                            const { value } = event.target;
                            setIsCheckingCatNum(true);
                            setCatalogueNumber(value);
                            setFieldValue('catalogueNumber', value);
                          }}
                          onBlur={handleBlur}
                          isInvalid={
                            (touched.catalogueNumber &&
                              !!errors.catalogueNumber) ||
                            (!catalogueNumberUniqueValidator.validate() &&
                              catalogueNumber)
                          }
                          isValid={
                            !errors.catalogueNumber &&
                            catalogueNumberUniqueValidator.validate() &&
                            catalogueNumber &&
                            !isCheckingCatNum &&
                            isCatNumUnique
                          }
                        />
                        {/* Feedback for valid or invalid input. */}
                        {catalogueNumberUniqueValidator.validate() &&
                          catalogueNumber &&
                          !isCheckingCatNum && (
                            <Form.Control.Feedback type="valid">
                              Looks good!
                            </Form.Control.Feedback>
                          )}
                        <Form.Control.Feedback type="invalid">
                          {errors.catalogueNumber}
                          {!catalogueNumberUniqueValidator.validate() &&
                            !isCheckingCatNum &&
                            catalogueNumber &&
                            catalogueNumberUniqueValidator.text}
                        </Form.Control.Feedback>
                        {isCheckingCatNum && <Form.Text>Checking...</Form.Text>}
                      </Form.Group>
                      <Form.Group
                        as={Col}
                        md="8"
                        controlId="formProductCatgeory"
                        className="field-margin"
                      >
                        <Form.Label>
                          Product Category <RequiredAsteriskComponent />
                        </Form.Label>
                        {/* Input for product category with validation feedback. */}
                        <Form.Select
                          name="category"
                          value={values.category}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isValid={values?.category}
                          isInvalid={touched.category && !values.category}
                        >
                          <option value="" disabled>
                            -- Select product category --
                          </option>
                          {PRODUCT_CATEGORIES.map((choice, index) => (
                            <option key={index} value={choice.value}>
                              {choice.label}
                            </option>
                          ))}
                        </Form.Select>
                        {/* Feedback for valid or invalid input. */}
                        <Form.Control.Feedback type="valid">
                          Looks good!
                        </Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          {errors.category}
                        </Form.Control.Feedback>
                      </Form.Group>
                      {/* Kit Components Management - Only show for Kit category */}
                      {values.category === 'Kit' && (
                        <Form.Group className="field-margin">
                          <Form.Label>Kit Components</Form.Label>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="text-muted">
                              {(kitComponents || []).length === 0
                                ? 'No components added yet'
                                : `${(kitComponents || []).length} component${
                                    (kitComponents || []).length === 1
                                      ? ''
                                      : 's'
                                  } added`}
                            </span>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={handleAddKitComponent}
                            >
                              Add Components
                            </Button>
                          </div>

                          {(kitComponents || []).length > 0 && (
                            <div className="border rounded p-3 bg-light">
                              <h6 className="mb-3">Current Components:</h6>
                              <div className="row">
                                {kitComponents.map((component, index) => (
                                  <div key={index} className="col-12 mb-2">
                                    <div className="d-flex justify-content-between align-items-center p-2 border rounded bg-white">
                                      <div>
                                        <strong>
                                          {component.component?.name ||
                                            'Unknown Component'}
                                        </strong>
                                        <br />
                                        <small className="text-muted">
                                          Quantity: {component.quantity_per_kit}{' '}
                                          |
                                          {component.is_required
                                            ? ' Required'
                                            : ' Optional'}
                                        </small>
                                      </div>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() =>
                                          handleEditKitComponent(component)
                                        }
                                      >
                                        Edit
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <Form.Text className="text-muted">
                            Add components that make up this kit. Each component
                            can be marked as required or optional.
                          </Form.Text>
                        </Form.Group>
                      )}
                      <Form.Group
                        as={Col}
                        md="8"
                        controlId="formProductUnit"
                        className="field-margin"
                      >
                        <Form.Label>
                          Measurement Unit <RequiredAsteriskComponent />
                        </Form.Label>
                        {/* Input for measurement unit with validation feedback. */}
                        <Form.Select
                          name="unit"
                          value={values.unit}
                          onChange={event => {
                            handleChange(event);
                            const { value } = event.target;
                            if (value === 'Package' || value === 'Box') {
                              setShowSubUnitsField(true);
                            }
                          }}
                          onBlur={handleBlur}
                          isValid={values?.unit}
                          isInvalid={touched.unit && !values.unit}
                        >
                          <option value="" disabled>
                            -- Select measurement unit --
                          </option>
                          {PRODUCT_MEASUREMENT_UNITS.map((choice, index) => (
                            <option key={index} value={choice.value}>
                              {choice.label}
                            </option>
                          ))}
                        </Form.Select>
                        {/* Feedback for valid or invalid input. */}
                        <Form.Control.Feedback type="valid">
                          Looks good!
                        </Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          {errors.unit}
                        </Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group
                        controlId="formProductUnitQuantity"
                        className="field-margin"
                      >
                        <Form.Label>
                          Volume / Quantity <RequiredAsteriskComponent />
                        </Form.Label>
                        {/* Input for unit quantity with validation feedback. */}
                        <NumberInput
                          name="unitQuantity"
                          value={values.unitQuantity}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isValid={!errors.unitQuantity && values.unitQuantity}
                          isInvalid={
                            (touched?.unitQuantity && !values?.unitQuantity) ||
                            (errors?.unitQuantity &&
                              errors?.unitQuantity !==
                                'Unit volume/quantity is required' &&
                              values?.unitQuantity)
                          }
                        />
                        {/* Feedback for valid or invalid input. */}
                        <Form.Control.Feedback type="valid">
                          Looks good!
                        </Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          {errors.unitQuantity}
                        </Form.Control.Feedback>
                      </Form.Group>{' '}
                      {/* Multi-Component Switch and Container */}
                      {values.supportsMultiComponents ? (
                        <div className="border rounded p-3 mb-3">
                          {/* Multi-Component Switch */}
                          <Form.Group className="mb-3">
                            <Form.Check
                              type="switch"
                              id="supportsMultiComponents"
                              label="Multi-Component Product"
                              checked={values.supportsMultiComponents}
                              onChange={handleChange}
                              name="supportsMultiComponents"
                            />
                            <div
                              className="alert alert-info d-flex align-items-start mt-2"
                              role="alert"
                            >
                              <i className="fa fa-info-circle me-2 mt-1"></i>
                              <div>
                                <strong>Multi-Component Product:</strong> Enable
                                this if this product is made up of related
                                products with their own CAT# (e.g., a kit
                                containing multiple individual products, a
                                bundle with separate catalog items, etc.)
                              </div>
                            </div>
                          </Form.Group>

                          <Form.Group className="field-margin">
                            <Form.Label>
                              Multi-Component Related Products
                            </Form.Label>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="text-muted">
                                {(multiComponents || []).length === 0
                                  ? 'No related products added yet'
                                  : `${
                                      (multiComponents || []).length
                                    } related product${
                                      (multiComponents || []).length === 1
                                        ? ''
                                        : 's'
                                    } added`}
                              </span>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleAddMultiComponent}
                              >
                                Add Related Products
                              </Button>
                            </div>

                            {(multiComponents || []).length > 0 && (
                              <div className="border rounded p-3 bg-light">
                                <h6 className="mb-3">
                                  Current Related Products:
                                </h6>
                                <div className="row">
                                  {multiComponents.map((component, index) => (
                                    <div key={index} className="col-12 mb-2">
                                      <div className="d-flex justify-content-between align-items-center p-2 border rounded bg-white">
                                        <div>
                                          <strong>
                                            {component.component?.name ||
                                              'Unknown Component'}
                                          </strong>
                                          <br />
                                          <small className="text-muted">
                                            CAT#:{' '}
                                            {component.component?.cat_num ||
                                              'N/A'}{' '}
                                            | Quantity:{' '}
                                            {component.quantity_per_component} |{' '}
                                            {component.is_required
                                              ? 'Required'
                                              : 'Optional'}
                                          </small>
                                        </div>
                                        <div className="d-flex gap-1">
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() =>
                                              handleEditMultiComponent(
                                                component
                                              )
                                            }
                                          >
                                            Edit
                                          </Button>
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() =>
                                              handleMultiComponentUpdate(
                                                'remove',
                                                component.component.id
                                              )
                                            }
                                          >
                                            Remove
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div
                              className="alert alert-info d-flex align-items-start"
                              role="alert"
                            >
                              <i className="fa fa-info-circle me-2 mt-1"></i>
                              <div>
                                <strong>Related Products:</strong> Add related
                                products with their own CAT# that make up this
                                multi-component product.
                              </div>
                            </div>
                          </Form.Group>
                        </div>
                      ) : (
                        /* Multi-Component Switch - No border when OFF */
                        <Form.Group className="mb-3">
                          <Form.Check
                            type="switch"
                            id="supportsMultiComponents"
                            label="Multi-Component Product"
                            checked={values.supportsMultiComponents}
                            onChange={handleChange}
                            name="supportsMultiComponents"
                          />
                          <div
                            className="alert alert-info d-flex align-items-start mt-2"
                            role="alert"
                          >
                            <i className="fa fa-info-circle me-2 mt-1"></i>
                            <div>
                              <strong>Multi-Component Product:</strong> Enable
                              this if this product is made up of related
                              products with their own CAT# (e.g., a kit
                              containing multiple individual products, a bundle
                              with separate catalog items, etc.)
                            </div>
                          </div>
                        </Form.Group>
                      )}
                      {/* Sub-Units Switch and Container */}
                      {showSubUnitsField ? (
                        <div className="border rounded p-3 mb-3">
                          {/* Sub-Units Switch */}
                          <Form.Group className="mb-3">
                            <Form.Check
                              type="switch"
                              id="hasSubUnits"
                              label="Product has sub units?"
                              checked={showSubUnitsField}
                              onChange={toggleShowSubUnitsField}
                            />
                            <div
                              className="alert alert-info d-flex align-items-start mt-2"
                              role="alert"
                            >
                              <i className="fa fa-info-circle me-2 mt-1"></i>
                              <div>
                                <strong>Sub-Units:</strong> Enable this if your
                                product is a single CAT# with multiple units
                                (e.g., a box with 20 bags of 50 tubes, a package
                                containing 10 vials, etc.)
                              </div>
                            </div>
                          </Form.Group>

                          <Form.Group
                            controlId="formProductUnitsPerSubUnit"
                            className="field-margin"
                          >
                            <Form.Label>Units Per Sub Unit</Form.Label>
                            {/* Input for unit quantity with validation feedback. */}
                            <NumberInput
                              name="unitsPerSubUnit"
                              value={values.unitsPerSubUnit}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isValid={
                                !errors.unitsPerSubUnit &&
                                values.unitsPerSubUnit
                              }
                              isInvalid={
                                showSubUnitsField &&
                                errors?.unitsPerSubUnit &&
                                values?.unitsPerSubUnit
                              }
                            />
                            {/* Feedback for valid or invalid input. */}
                            <Form.Control.Feedback type="valid">
                              Looks good!
                            </Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                              {errors.unitsPerSubUnit}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </div>
                      ) : (
                        /* Sub-Units Switch - No border when OFF */
                        <Form.Group className="mb-3">
                          <Form.Check
                            type="switch"
                            id="hasSubUnits"
                            label="Product has sub units?"
                            checked={showSubUnitsField}
                            onChange={toggleShowSubUnitsField}
                          />
                          <div
                            className="alert alert-info d-flex align-items-start mt-2"
                            role="alert"
                          >
                            <i className="fa fa-info-circle me-2 mt-1"></i>
                            <div>
                              <strong>Sub-Units:</strong> Enable this if your
                              product is a single CAT# with multiple units
                              (e.g., a box with 20 bags of 50 tubes, a package
                              containing 10 vials, etc.)
                            </div>
                          </div>
                        </Form.Group>
                      )}
                      <Form.Group
                        as={Col}
                        md="8"
                        controlId="formProductStorage"
                        className="field-margin mt-3"
                      >
                        <Form.Label>
                          Storage Conditions <RequiredAsteriskComponent />
                        </Form.Label>
                        {/* Input for storage condition with validation feedback. */}
                        <Form.Select
                          name="storageConditions"
                          value={values.storageConditions}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isValid={values.storageConditions}
                          isInvalid={
                            touched.storageConditions &&
                            !values.storageConditions
                          }
                        >
                          <option value="" disabled>
                            -- Select storage condition --
                          </option>
                          {PRODUCT_STORAGE_OPTIONS.map((choice, index) => (
                            <option key={index} value={choice.value}>
                              {choice.label}
                            </option>
                          ))}
                        </Form.Select>
                        {/* Feedback for valid or invalid input. */}
                        <Form.Control.Feedback type="valid">
                          Looks good!
                        </Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          {errors.storageConditions}
                        </Form.Control.Feedback>
                      </Form.Group>
                      {values.supplierCatItem !== true && (
                        <Form.Group
                          controlId="formProductAliquotsLocation"
                          className="field-margin"
                        >
                          <Form.Label>Aliquots Location</Form.Label>
                          <Form.Control
                            type="text"
                            name="aliquotsLocation"
                            value={values.aliquotsLocation}
                            onChange={event => {
                              const { value } = event.target;
                              if (value.length <= 255) {
                                handleChange(event);
                              }
                            }}
                            onBlur={handleBlur}
                            maxLength={255}
                            isValid={
                              touched.aliquotsLocation &&
                              !errors.aliquotsLocation &&
                              values.aliquotsLocation
                            }
                            isInvalid={
                              touched.aliquotsLocation &&
                              !!errors.aliquotsLocation
                            }
                          />
                          <Form.Control.Feedback type="valid">
                            Looks good!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.aliquotsLocation}
                          </Form.Control.Feedback>
                          <Form.Text>{`${values?.aliquotsLocation?.length || 0}/255`}</Form.Text>
                        </Form.Group>
                      )}
                      <Form.Group
                        controlId="formProductStock"
                        className="field-margin"
                      >
                        <Form.Label>Stock</Form.Label>
                        {/* Kit package count is derived after BOM + component inventory. */}
                        <NumberInput
                          name="stock"
                          integerOnly
                          value={values.stock}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={values.category === 'Kit'}
                          isValid={
                            values.category !== 'Kit' &&
                            values.stock &&
                            !errors.stock &&
                            values.stock
                          }
                          isInvalid={
                            values.category !== 'Kit' &&
                            errors?.stock &&
                            values?.stock
                          }
                        />
                        {values.category === 'Kit' ? (
                          <Form.Text className="text-muted">
                            Complete kits on hand are derived from the recipe
                            and component inventory. A value entered here is
                            ignored by the API.
                          </Form.Text>
                        ) : (
                          <>
                            <Form.Control.Feedback type="valid">
                              Looks good!
                            </Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                              {errors.stock}
                            </Form.Control.Feedback>
                          </>
                        )}
                      </Form.Group>
                      <Form.Group
                        controlId="formProductPrice"
                        className="field-margin"
                      >
                        <Form.Label>Price</Form.Label>
                        {/* Input for price with validation feedback. */}
                        <NumberInput
                          name="price"
                          value={values.price}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isValid={
                            values.price && !errors.price && values.price
                          }
                          isInvalid={errors?.price && values?.price}
                        />
                        {/* Feedback for valid or invalid input. */}
                        <Form.Control.Feedback type="valid">
                          Looks good!
                        </Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          {errors.price}
                        </Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group className="d-flex flex-row">
                        <Form.Label className="me-3">
                          Discount given?
                        </Form.Label>
                        <Form.Switch
                          checked={showDiscountField}
                          onChange={toggleShowDiscountField}
                        />
                      </Form.Group>
                      {showDiscountField ? (
                        <Form.Group
                          controlId="formProductDiscount"
                          className="field-margin"
                        >
                          <Form.Label>Discount Percentage</Form.Label>
                          {/* Input for unit quantity with validation feedback. */}
                          <NumberInput
                            name="discount"
                            value={values.discount}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isValid={!errors.discount && values.discount}
                            isInvalid={
                              showDiscountField &&
                              errors?.discount &&
                              values?.discount
                            }
                          />

                          {/* Feedback for valid or invalid input. */}
                          <Form.Control.Feedback type="valid">
                            Looks good!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.discount}
                          </Form.Control.Feedback>
                          {values.price &&
                            values.discount &&
                            !errors?.discount && (
                              <Form.Text style={{ fontSize: 20 }}>
                                <DiscountedPriceComponent
                                  product={{
                                    price: values.price,
                                    discount: values.discount,
                                  }}
                                />
                              </Form.Text>
                            )}
                        </Form.Group>
                      ) : null}
                      <Form.Group
                        as={Col}
                        md="8"
                        controlId="formProductCurrency"
                        className="field-margin mt-4"
                      >
                        <Form.Label>Currency</Form.Label>
                        {/* Input for measurement unit with validation feedback. */}
                        <Form.Select
                          name="currency"
                          value={values.currency}
                          onChange={handleChange}
                          isValid={values.currency}
                        >
                          <option value="" disabled>
                            -- Select currency --
                          </option>
                          <option value="NIS">NIS</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                        </Form.Select>
                        {/* Feedback for valid or invalid input. */}
                        <Form.Control.Feedback type="valid">
                          Looks good!
                        </Form.Control.Feedback>
                      </Form.Group>
                      {!isSupplier && supplierList.length > 0 && (
                        <>
                          <Form.Group
                            as={Col}
                            md="8"
                            controlId="formProductSupplier"
                            className="field-margin"
                          >
                            <Form.Label>
                              Supplier <RequiredAsteriskComponent />
                            </Form.Label>
                            {/* Input for supplier with validation feedback. */}
                            <DropdownSelect
                              optionsList={supplierList}
                              placeholder={'Select Supplier'}
                              selectedValue={
                                supplierList.find(
                                  p => p.value === values.supplier
                                ) || null
                              }
                              setSelectedValue={option => {
                                const supplierId = option?.value || '';
                                setFieldValue('supplier', supplierId);
                                setSupplierId(supplierId);
                              }}
                              touched={touched.supplier}
                              error={errors.supplier}
                              isInvalid={touched.supplier && !values.supplier}
                              onBlur={() => setFieldTouched('supplier', true)}
                              disabledOptions={[]}
                            />
                            {/* Feedback for valid or invalid input. */}
                            <Form.Control.Feedback type="valid">
                              Looks good!
                            </Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                              {errors.supplier}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </>
                      )}
                      <Form.Group
                        as={Col}
                        md="8"
                        controlId="formProductManufacturer"
                        className="field-margin"
                      >
                        <Form.Label>Related Manufacturer</Form.Label>
                        {/* Input for manufacturer with validation feedback. */}
                        <DropdownSelect
                          optionsList={manufacturerList}
                          placeholder={
                            !productObj && !supplierId
                              ? 'Select a supplier first'
                              : 'Related Manufacturer'
                          }
                          selectedValue={
                            manufacturerList.find(
                              m => m.value === values.manufacturer
                            ) || null
                          }
                          setSelectedValue={option =>
                            setFieldValue('manufacturer', option?.value || '')
                          }
                          touched={touched.manufacturer}
                          error={errors.manufacturer}
                          isInvalid={
                            touched.manufacturer && !values.manufacturer
                          }
                          onBlur={() => setFieldTouched('manufacturer', true)}
                          disabledOptions={[]}
                          disabled={!productObj && !supplierId}
                        />
                        {/* Feedback for valid or invalid input. */}
                        <Form.Control.Feedback type="valid">
                          Looks good!
                        </Form.Control.Feedback>
                        {fetchingManufacturersRef.current && (
                          <Spinner size="sm" />
                        )}
                        {/* Overlay trigger to notfiy the user that this supplier has no manufacturers related to it */}
                        {supplierId &&
                          manufacturerList.length === 0 &&
                          !fetchingManufacturersRef.current && (
                            <Form.Text>
                              No Manufacturers related to this supplier{' '}
                              <OverlayTrigger
                                overlay={
                                  <Tooltip
                                    id={`tooltip-no-related-manufacturers-info`}
                                    className="custom-tooltip"
                                  >
                                    Update this supplier's related manufacturers
                                    via the{' '}
                                    <Link to="/suppliers">suppliers page</Link>
                                  </Tooltip>
                                }
                                placement="right"
                                delay={{ show: 50, hide: 2000 }}
                              >
                                <InfoCircle />
                              </OverlayTrigger>
                            </Form.Text>
                          )}
                      </Form.Group>
                      <Form.Group
                        controlId="formProductUrl"
                        className="field-margin"
                      >
                        <Form.Label>Product Link</Form.Label>
                        {/* Input for product link with validation feedback. */}
                        <Form.Control
                          type="text"
                          name="productUrl"
                          value={values.productUrl}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isValid={!errors.productUrl && values.productUrl}
                          isInvalid={errors?.productUrl && values?.productUrl}
                        />
                        {/* Feedback for valid or invalid input. */}
                        <Form.Control.Feedback type="valid">
                          Looks good!
                        </Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          {errors.productUrl}
                        </Form.Control.Feedback>
                      </Form.Group>
                      {/* Dynamic rendering of product images. */}
                      <div className="field-margin">
                        {(images || []).map((image, index) => {
                          const isSavedApiImage = Boolean(image.image_url);
                          const localPreviewUrl =
                            !isSavedApiImage && image.file
                              ? URL.createObjectURL(image.file)
                              : null;

                          return (
                            <div key={image.id}>
                              <div className={`${index > 0 && 'mt-2'}`}>
                                {isSavedApiImage ? (
                                  <S3MediaLink
                                    url={image.image_url}
                                    productImageId={image.id}
                                    title="Open product image"
                                    className="me-3"
                                  >
                                    <S3MediaImage
                                      url={image.image_url}
                                      productImageId={image.id}
                                      alt={`product-${values.catalogueNumber}-${image.id}`}
                                      width={200}
                                    />
                                  </S3MediaLink>
                                ) : (
                                  <a
                                    href={localPreviewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="me-3"
                                  >
                                    <img
                                      src={localPreviewUrl}
                                      alt={`product-${values.catalogueNumber}-${image.id}`}
                                      width="200"
                                    />
                                  </a>
                                )}
                                <TrashFill
                                  size={20}
                                  color={'red'}
                                  onClick={() => {
                                    setFieldValue('productImages', '');
                                    handleDeleteImage(image.id);
                                  }}
                                  style={{ cursor: 'pointer' }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Upload field for product images. */}
                      <Form.Group
                        controlId="formProductImages"
                        className="field-margin"
                      >
                        <Form.Label>
                          Upload Product Images (jpg, jpeg, png, webp, gif,
                          avif)
                        </Form.Label>
                        <Form.Control
                          type="file"
                          multiple
                          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/avif"
                          name="productImages"
                          onChange={event => {
                            const files = Array.from(event.target.files);
                            handleFileChange(files);
                            setFieldValue(
                              'productImages',
                              files.length ? files : null
                            );
                          }}
                          isValid={
                            values.productImages || (images || []).length
                          }
                        />
                        {/* Feedback for valid or invalid input. */}
                        <Form.Control.Feedback type="valid">
                          Looks good!
                        </Form.Control.Feedback>
                      </Form.Group>
                      <Form.Label>Notes</Form.Label>
                      <Form.Group controlId="formProductNotes">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          placeholder="Enter note"
                          name={'notes'}
                          value={values.notes}
                          onChange={event => {
                            const { value } = event.target;
                            if (value.length <= 255) {
                              handleChange(event);
                            }
                          }}
                          onBlur={handleBlur}
                          isValid={
                            touched.notes && !errors.notes && values.notes
                          }
                          isInvalid={errors?.notes && values?.notes}
                        />
                        {/* Feedback for valid or invalid input. */}
                        <Form.Control.Feedback type="valid">
                          Looks good!
                        </Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          {errors.productUrl}
                        </Form.Control.Feedback>
                        <Form.Text>{`${values?.notes?.length}/255`}</Form.Text>
                      </Form.Group>
                      {/* Kit Description Field - Only show for Kit category */}
                      {values.category === 'Kit' && (
                        <Form.Group className="field-margin">
                          <Form.Label>Kit Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Describe what this kit contains..."
                            name="kitDescription"
                            value={values.kitDescription || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <Form.Text className="text-muted">
                            Optional description of what the kit contains and
                            its intended use.
                          </Form.Text>
                        </Form.Group>
                      )}
                      <Form.Group className="mt-2">
                        <Form.Label className="me-3">
                          Expiry Relevant? (choose 'Yes' if not certain)
                          <RequiredAsteriskComponent />
                        </Form.Label>
                        <div className="d-flex flex-column">
                          <Form.Select
                            name="expiryRelevant"
                            value={values.expiryRelevant}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isValid={values?.expiryRelevant}
                            isInvalid={
                              touched.expiryRelevant && !values.expiryRelevant
                            }
                          >
                            <option value="" disabled>
                              -- Select expiry category --
                            </option>
                            {EXPIRY_CATEGORIES.map((choice, index) => (
                              <option key={index} value={choice.value}>
                                {choice.label}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="valid">
                            Looks good!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.expiryRelevant}
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer className="procure-modal__footer">
                  {/* Conditional rendering of a spinner or submit button based on submission state. */}
                  {isSubmitting ? (
                    <Button variant="primary" className="glass-primary-btn" disabled>
                      <Spinner
                        size="sm"
                        as="span"
                        animation="border"
                        role="status"
                        aria-hidden="true"
                      />
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      className="glass-primary-btn"
                      disabled={
                        !isValid ||
                        !catalogueNumberUniqueValidator.validate() ||
                        isCheckingCatNum ||
                        (!productObj && !dirty)
                      }
                      type="submit"
                    >
                      {productObj ? 'Save' : 'Create'}
                    </Button>
                  )}

                  <Button
                    variant="secondary"
                    className="procure-modal__close-btn"
                    onClick={handleClose}
                  >
                    Close
                  </Button>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};
export default ProductModal;
